import React from 'react';
import { DeleteOutlined, PlusCircleOutlined, SisternodeOutlined } from '@ant-design/icons';
import ToastUtils from '../../../../utils/ToastUtils';
import DeleteIcon from './DeleteIcon';
import './AIPPT.scss';

const AIPPT = ({
    data = {},
    onEdit,
    onAddSection,
    onDeleteSection,
    onGenerate,
    onSave,
    editable = true,
    showGenerateButton = true, // 新增属性控制生成按钮显示
    isReadOnly = false // 🔥 新增属性：只读状态
}) => {
    const [activeInput, setActiveInput] = React.useState(null); // 跟踪当前激活的输入框
    const blurTimeoutRef = React.useRef(null); // 用于管理延迟清除的定时器
    const inputRefs = React.useRef({}); // 存储所有输入框的引用
    const pendingFocusRef = React.useRef(null); // 存储待聚焦的输入框ID
    const prevDataRef = React.useRef(data); // 存储上一次的数据
    const containerRef = React.useRef(null); // 存储容器的引用

    // 监听数据变化，实现延迟聚焦
    React.useEffect(() => {
        // 检查是否有待聚焦的输入框
        if (pendingFocusRef.current) {
            const inputId = pendingFocusRef.current;
            pendingFocusRef.current = null;

            // 延迟聚焦，确保DOM已更新
            setTimeout(() => {
                const inputElement = inputRefs.current[inputId];
                if (inputElement) {
                    inputElement.focus();
                    handleInputFocus(inputId);
                }
            }, 150); // 给数据更新和API请求更多时间
        }

        // 更新上一次数据引用
        prevDataRef.current = data;
    }, [data]); // 监听data变化

    // 动态调整连线长度
    React.useEffect(() => {
        const adjustConnectorLines = () => {
            if (!containerRef.current) return;

            const catalogSections = containerRef.current.querySelectorAll('.catalog-section-v2');

            catalogSections.forEach((section, catalogIndex) => {
                const levelSection = section.querySelector('.level-section:not(.sub)');
                const firstSubSection = section.querySelector('.level-section.sub:first-child');

                if (levelSection && firstSubSection) {
                    const levelHeight = levelSection.offsetHeight;
                    const verticalLine = firstSubSection.querySelector('.vertical-line');

                    if (verticalLine) {
                        // 计算需要的垂直线长度
                        const baseTop = -16; // 基础向上延伸
                        const additionalHeight = Math.max(0, levelHeight - 44); // 44是基础一级大纲高度
                        const newTop = baseTop - additionalHeight;
                        const newHeight = `calc(100% + ${additionalHeight}px)`;

                        verticalLine.style.top = `${newTop}px`;
                        verticalLine.style.height = newHeight;
                    }
                }
            });
        };

        // 延迟执行，确保DOM渲染完成
        const timer = setTimeout(adjustConnectorLines, 100);

        return () => clearTimeout(timer);
    }, [data, activeInput, editable]); // 当数据、激活状态或编辑状态改变时重新调整

    // 组件卸载时清理定时器
    React.useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    // 处理输入框获得焦点
    const handleInputFocus = (inputId) => {
        // 清除之前的延迟清除定时器
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }
        setActiveInput(inputId);
    };

    // 处理输入框失去焦点
    const handleInputBlur = () => {
        // 设置延迟清除，给按钮点击留出时间
        blurTimeoutRef.current = setTimeout(() => {
            setActiveInput(null);
            blurTimeoutRef.current = null;
        }, 150);
    };

    // 处理添加操作
    const handleAddSection = (catalogIndex, subIndex, type) => {
        if (onAddSection) {
            // 根据添加类型确定新输入框的ID
            let newInputId;
            if (type === 'sub') {
                // 添加子级：新的二级大纲会添加到子目录的末尾
                const currentSubLength = data.catalogs[catalogIndex]?.sub_catalog?.length || 0;
                newInputId = `sub-${catalogIndex}-${currentSubLength}`;
            } else if (type === 'sibling') {
                // 添加同级：新的一级大纲会添加到catalogs数组的末尾
                const currentCatalogsLength = data.catalogs?.length || 0;
                newInputId = `catalog-${currentCatalogsLength}`;
            } else {
                // 默认添加子级
                const subLength = data.catalogs[catalogIndex]?.sub_catalog?.length || 0;
                newInputId = `sub-${catalogIndex}-${subLength}`;
            }

            // 存储待聚焦的输入框ID，将在数据更新后聚焦
            pendingFocusRef.current = newInputId;

            // 调用父组件的添加函数
            onAddSection(catalogIndex, subIndex, type);
        }
    };

    // 从上到下逐个验证输入框，遇到第一个空字段就返回
    const validateInputsSequentially = () => {
        // 检查标题
        if (!data.title || data.title.trimStart() === '') {
            return { isEmpty: true, field: '标题' };
        }

        // 检查一级大纲
        if (data.catalogs && Array.isArray(data.catalogs)) {
            for (let catalogIndex = 0; catalogIndex < data.catalogs.length; catalogIndex++) {
                const catalog = data.catalogs[catalogIndex];

                // 检查一级大纲
                if (!catalog.catalog || catalog.catalog.trimStart() === '') {
                    return { isEmpty: true, field: `一级大纲` };
                }

                // 检查该一级大纲下的所有二级大纲
                if (catalog.sub_catalog && catalog.sub_catalog.length > 0) {
                    for (let subIndex = 0; subIndex < catalog.sub_catalog.length; subIndex++) {
                        const subCatalog = catalog.sub_catalog[subIndex];
                        // 判断subCatalog是字符串还是对象
                        const subCatalogText = typeof subCatalog === 'string' ? subCatalog : subCatalog.catalog;
                        if (!subCatalogText || subCatalogText.trimStart() === '') {
                            return { isEmpty: true, field: `二级大纲` };
                        }
                    }
                }
            }
        }

        return { isEmpty: false };
    };

    // 处理生成按钮点击
    const handleGenerate = () => {
        const validation = validateInputsSequentially();

        if (validation.isEmpty) {
            ToastUtils.showToast(`${validation.field}不能为空`);
            return;
        }

        // 检查是否有内容超过500字
        const overLimitFields = [];

        // 检查标题
        if (data.title && data.title.length > 500) {
            overLimitFields.push('标题');
        }

        // 检查一级大纲
        if (data.catalogs && Array.isArray(data.catalogs)) {
            data.catalogs.forEach((catalog, catalogIndex) => {
                if (catalog.catalog && catalog.catalog.length > 500) {
                    overLimitFields.push(`一级大纲${catalogIndex + 1}`);
                }

                // 检查二级大纲
                if (catalog.sub_catalog && catalog.sub_catalog.length > 0) {
                    catalog.sub_catalog.forEach((subCatalog, subIndex) => {
                        // 判断subCatalog是字符串还是对象
                        const subCatalogText = typeof subCatalog === 'string' ? subCatalog : subCatalog.catalog;
                        if (subCatalogText && subCatalogText.length > 500) {
                            overLimitFields.push(`二级大纲${catalogIndex + 1}.${subIndex + 1}`);
                        }
                    });
                }
            });
        }

        if (overLimitFields?.length > 0) {
            // 优化提示信息：当超长字段过多时显示简化信息
            if (overLimitFields.length > 3) {
                ToastUtils.showToast(`有 ${overLimitFields.length} 个字段超过500字限制，请缩短内容`);
            } else {
                ToastUtils.showToast(`内容超过500字限制，请缩短：${overLimitFields.join('、')}`);
            }
            return;
        }

        // 所有输入框都有内容且未超过限制，继续生成
        if (onGenerate) {
            onGenerate(data);
        }
    };

    // 渲染可编辑内容或纯文本
    const renderContent = (type, content, catalogIndex, subIndex = null) => {
        const inputId = type === 'catalog' ? `catalog-${catalogIndex}` : `sub-${catalogIndex}-${subIndex}`;
        const isActive = activeInput === inputId;

        if (editable) {
            // 可编辑状态：显示输入框
            return (
                <input
                    ref={(el) => {
                        if (el) {
                            inputRefs.current[inputId] = el;
                        } else {
                            delete inputRefs.current[inputId];
                        }
                    }}
                    type="text"
                    value={content || ''}
                    onChange={(e) => {
                        const newValue = e.target.value;

                        // 限制输入不超过500字
                        if (newValue.length > 500) {
                            ToastUtils.showToast('输入内容不能超过500字，请缩短内容');
                            return;
                        }

                        if (onEdit) {
                            if (type === 'catalog') {
                                onEdit('catalog', catalogIndex, newValue);
                            } else if (type === 'sub') {
                                onEdit('sub', { catalogIndex, subIndex }, newValue);
                            }
                        }
                    }}
                    onFocus={() => handleInputFocus(inputId)}
                    onBlur={handleInputBlur}
                    className="content-input editable"
                    placeholder="输入内容"
                />
            );
        } else {
            // 只读状态：显示纯文本
            return (
                <div className="content-text readonly">
                    {content || '输入内容'}
                </div>
            );
        }
    };

    return (
        <div className="aippt-container-v2" ref={containerRef}>

            {/* 标题区域 */}
            <div className="title-section-v2">
                {editable ? (
                    <div className="title-input-group">
                        <span className="title-label-v2">标题</span>
                        <input
                            ref={(el) => {
                                if (el) {
                                    inputRefs.current['title'] = el;
                                } else {
                                    delete inputRefs.current['title'];
                                }
                            }}
                            type="text"
                            value={data.title || ''}
                            onChange={(e) => {
                                const newValue = e.target.value;

                                // 限制输入不超过500字
                                if (newValue.length > 500) {
                                    ToastUtils.showToast('输入内容不能超过500字，请缩短内容');
                                    return;
                                }

                                onEdit && onEdit('title', 0, newValue);
                            }}
                            onFocus={() => handleInputFocus('title')}
                            onBlur={handleInputBlur}
                            className="title-input-v2 editable"
                            placeholder="输入内容"
                        />
                    </div>
                ) : (
                    <div className="title-input-group">
                        <span className="title-label-v2">标题</span>
                        <div className="title-text-v2">
                            {data.title || '输入内容'}
                        </div>
                    </div>
                )}
            </div>

            {/* 目录内容区域 */}
            <div className="catalog-content-v2">
                {data.catalogs && data.catalogs.map((catalog, catalogIndex) => (
                    <div key={catalogIndex} className="catalog-section-v2">
                        {/* 一级大纲 */}
                        <div className="level-section">
                            <div className="level-label">一级大纲</div>
                            <div className="input-with-actions">
                                {renderContent('catalog', catalog.catalog, catalogIndex)}
                                {editable && activeInput === `catalog-${catalogIndex}` && (
                                    <div className="action-buttons-inline">
                                        {/* 添加子级按钮：当该一级大纲下的二级大纲少于30个时才显示 */}
                                        {(catalog.sub_catalog?.length || 0) < 30 && (
                                            <button
                                                className="action-btn-v2 add"
                                                onMouseDown={(e) => {
                                                    e.preventDefault(); // 防止触发输入框的blur事件
                                                    handleAddSection(catalogIndex, null, 'sibling');
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                title="添加子级（一级大纲）"
                                            >
                                                <PlusCircleOutlined />
                                            </button>
                                        )}
                                        {/* 添加同级按钮：当一级大纲总数少于30个时才显示 */}
                                        {data.catalogs?.length < 30 && (
                                            <button
                                                className="action-btn-v2 move"
                                                onMouseDown={(e) => {
                                                    e.preventDefault(); // 防止触发输入框的blur事件
                                                    handleAddSection(catalogIndex, null, 'sub');
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                title="添加同级（二级大纲）"
                                            >
                                                <SisternodeOutlined style={{ fontSize: '17px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
                                            </button>
                                        )}
                                        {/* 删除按钮：当一级大纲总数大于1个时才显示 */}
                                        {data.catalogs?.length > 1 && (
                                            <button
                                                className="action-btn-v2 delete"
                                                onMouseDown={(e) => {
                                                    e.preventDefault(); // 防止触发输入框的blur事件
                                                    onDeleteSection && onDeleteSection(catalogIndex);
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                }}
                                                title="删除"
                                                style={{fontSize: '20px'}}
                                            >
                                                <DeleteIcon size={16} />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 二级大纲 */}
                        {catalog.sub_catalog && catalog.sub_catalog.length > 0 && (
                            <div className="sub-catalogs-v2">
                                {catalog.sub_catalog.map((subCatalog, subIndex) => (
                                    <div key={subIndex} className="level-section sub">
                                        <div className="connector-line">
                                            <div className="vertical-line"></div>
                                            <div className="horizontal-line"></div>
                                        </div>
                                        <div className="level-label">二级大纲</div>
                                        <div className="input-with-actions">
                                            {renderContent('sub', subCatalog, catalogIndex, subIndex)}
                                            {editable && activeInput === `sub-${catalogIndex}-${subIndex}` && (
                                                <div className="action-buttons-inline">
                                                    {/* 添加按钮：当该一级大纲下的二级大纲少于30个时才显示 */}
                                                    {(catalog.sub_catalog?.length || 0) < 30 && (
                                                        <button
                                                            className="action-btn-v2 add"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault(); // 防止触发输入框的blur事件
                                                                handleAddSection(catalogIndex, subIndex);
                                                            }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                            }}
                                                            title="添加"
                                                        >
                                                            <PlusCircleOutlined />
                                                        </button>
                                                    )}
                                                    <button
                                                        className="action-btn-v2 delete"
                                                        onMouseDown={(e) => {
                                                            e.preventDefault(); // 防止触发输入框的blur事件
                                                            onDeleteSection && onDeleteSection(catalogIndex, subIndex);
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        title="删除"
                                                    >
                                                        <DeleteIcon size={16} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}

                {/* 空状态 */}
                {(!data.catalogs || data.catalogs.length === 0) && editable && (
                    <div className="empty-state-v2">
                        <div className="level-section">
                            <div className="level-label">一级大纲</div>
                            <div className="input-with-actions">
                                <input
                                    type="text"
                                    className="content-input"
                                    placeholder="输入内容"
                                />
                                <div className="action-buttons-inline">
                                    <button className="action-btn-v2 add"><PlusCircleOutlined /></button>
                                    <button className="action-btn-v2 move"><SisternodeOutlined style={{ fontSize: '17px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} /></button>
                                    <button className="action-btn-v2 delete"><DeleteIcon size={16} /></button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 生成按钮 - 根据showGenerateButton控制显示 */}
            {showGenerateButton && (
                <div className="generate-section-v2">
                    <button
                        className="generate-btn-v2"
                        onClick={handleGenerate}
                    >
                        开始生成
                    </button>
                </div>
            )}
        </div>
    );
};

export default AIPPT;
